import { useState, useCallback, useEffect } from 'react';
import { debounce as _debounce } from 'lodash';
import { isValidJSONObject } from '@ion/mrdash';
import useTrackWithUser from '@ion/api/hooks/useTrackWithUser';

const INVALID_JSON_ERROR = 'Invalid JSON Object';
const DEBOUNCE_MS = 800;

export default function useEditorState({ defaultValue, setValue, setHasValidInput, trackingEventName }) {
  // Telemetry
  const [trackEditInput] = useTrackWithUser(trackingEventName);

  // Sample Events State
  const [selectedSampleEventId, setSelectedSampleEventId] = useState('');

  // Input Editor State
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentValue, setCurrentValue] = useState(defaultValue ?? '');
  const [editorKey, setEditorKey] = useState(0);

  // Input Editor Methods
  function validateAndSetInput(value) {
    setCurrentValue(value);
    setValue(value);

    setSelectedSampleEventId('');

    if (!value) {
      setHasValidInput(false);
      setErrorMessage(null);
    } else if (isValidJSONObject(value)) {
      setHasValidInput(true);
      setErrorMessage(null);
    } else {
      setHasValidInput(false);
      setErrorMessage(INVALID_JSON_ERROR);
    }
  }

  function onSetInput(event) {
    validateAndSetInput(event.target.value);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSetInput = useCallback(
    _debounce(event => {
      onSetInput(event);
      trackEditInput();
    }, DEBOUNCE_MS),
    []
  );

  // Input Editor Props
  const onInputBlur = event => {
    debouncedOnSetInput.cancel();
    onSetInput(event);
  };

  const onInputChange = event => {
    if (errorMessage) {
      setErrorMessage(null);
    }
    debouncedOnSetInput(event);
  };

  // Sample Event Props
  const loadSampleEvent = useCallback(
    ({ id, eventJson }) => {
      setCurrentValue(eventJson);
      setValue(eventJson);
      setHasValidInput(true);
      setErrorMessage(null);
      setSelectedSampleEventId(id);
      setEditorKey(prev => prev + 1);
    },
    [setValue, setHasValidInput]
  );

  const onSampleEventChange = useCallback(
    ({ eventId, eventJson }) => {
      const shouldResetSelect = eventId === selectedSampleEventId && eventJson !== currentValue;
      if (shouldResetSelect) {
        setSelectedSampleEventId('');
      }
    },
    [currentValue, selectedSampleEventId]
  );

  // Cleanup
  useEffect(() => {
    return () => {
      debouncedOnSetInput.cancel();
    };
  }, [debouncedOnSetInput]);

  return {
    currentValue,
    editorKey,
    errorMessage,
    selectedSampleEventId,
    onInputChange,
    onInputBlur,
    onSampleEventChange,
    loadSampleEvent,
  };
}
